import React, { useEffect, useState } from "react";
import { EarningsCalendarResponse, Theme } from "./types";
import { EarningsCard } from "./EarningsCard";
import { formatDate } from "./utils";
import { baseApi } from "@/api/baseApi";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { useUserContext } from "@/contexts/userContext";
import { useRouter } from "next/router";
import { useToast } from "@/components/ui/use-toast";
import { usePostCopyStrategy } from "@/api/strategyApi";
import { randomString } from "@/helpers/helperFunctions";
import { useAppDetails } from "@/contexts/AppDetailsContext";
import { Card, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import Link from "next/link";
import { ExternalLinkIcon } from "@radix-ui/react-icons";
import { useTranslation } from "react-i18next";

interface EarningsCalendarWidgetProps {
  theme?: Theme; // Kept for interface compatibility
  apiUrl?: string;
  width?: string | number;
  className?: string;
  badgeTitle?: string;
  onBadgeClick?: () => void;
}

export const EarningsCalendarWidget: React.FC<EarningsCalendarWidgetProps> = ({
  apiUrl = "/get_earnings_calendar",
  width = "100%",
  className,
  badgeTitle,
  onBadgeClick,
}) => {
  const { t } = useTranslation("build-page");
  const [data, setData] = useState<EarningsCalendarResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [impactFilter, setImpactFilter] = useState("all");
  const [logos, setLogos] = useState<Record<string, string>>({});
  const { details } = useAppDetails();
  const { user } = useUserContext();
  const router = useRouter();
  const { toast } = useToast();
  const {
    mutateAsync: copyStrategy,
    data: copyStrategyData,
    isSuccess: isCopySuccess,
  } = usePostCopyStrategy();
  const [loadingTicker, setLoadingTicker] = useState<string | null>(null);
  const handleTrade = async (ticker: string) => {
    if (!user) {
      if (typeof window !== "undefined") {
        window.open("https://app.trylevel2.com/create-new-account", "_top");
      } else {
        router.push("https://app.trylevel2.com/create-new-account");
      }
      return;
    }

    try {
      setLoadingTicker(ticker);
      // Using a placeholder for keygen_original as discussed in implementation plan.
      // In a real scenario, this would be a specific strategy key.
      const DEFAULT_STRATEGY_KEY = "296b4612-03f2-4adf-986a-d6bee906cda7"; // Example default key

      const res = await copyStrategy({
        userId: user?.user_id,
        key: DEFAULT_STRATEGY_KEY,
        symbol: ticker,
      });

      if (res?.data?.message) {
        toast({
          title: t("Success"),
          description: res.data.message,
        });
        // Removed redundant redirection to /strategy to allow the useEffect's 2s delay redirection to edit-strategy
      }
    } catch (error: any) {
      console.error("Error copying strategy:", error);
      setLoadingTicker(null);
      if (
        error.response?.data?.detail == "Invalid Session. Please Login again."
      ) {
        window.open("https://app.trylevel2.com", "_top");
      } else {
        toast({
          title: t("Error"),
          description:
            error.response?.data?.detail || t("Failed to initiate trade."),
          variant: "destructive",
        });
      }
    }
  };

  useEffect(() => {
    const fetchLogos = async (symbols: string[]) => {
      if (symbols.length === 0) return;
      try {
        const response = await baseApi.get(
          `/get_logos?symbols=${symbols.join(",")}`,
        );
        if (response.data?.data) {
          setLogos(response.data.data);
        }
      } catch (err) {
        console.error("Error fetching logos:", err);
      }
    };

    if (data) {
      const symbols = new Set<string>();
      Object.values(data).forEach((events) => {
        events.forEach((event) => symbols.add(event.ticker));
      });
      fetchLogos(Array.from(symbols));
    }
  }, [data]);

  useEffect(() => {
    if (isCopySuccess && copyStrategyData && user) {
      toast({
        title: t("Strategy copied successfully"),
        variant: "default",
      });
      setTimeout(() => {
        const editUrl =
          "/edit-strategy/" +
          encodeURIComponent(user?.user_id) +
          "/" +
          copyStrategyData?.data?.id +
          randomString(5) +
          "/" +
          user.token +
          "/false" +
          `?ref=${router.asPath}&locale=${router.query.locale || "en"}`;
        setLoadingTicker(null);

        if (typeof window !== "undefined") {
          window.open(editUrl, "_top");
        } else {
          router.push(editUrl);
        }
      }, 2000);
    }
  }, [isCopySuccess]);

  useEffect(() => {
    const fetchEarnings = async () => {
      try {
        setLoading(true);
        const response = await baseApi.get(apiUrl);
        const jsonData: EarningsCalendarResponse = response.data;

        // Sort events internally by time
        const sortedData: EarningsCalendarResponse = {};
        Object.keys(jsonData)
          .sort()
          .forEach((date) => {
            if (Array.isArray(jsonData[date])) {
              sortedData[date] = [...jsonData[date]].sort((a, b) => {
                return (a.time || "").localeCompare(b.time || "");
              });
            } else {
              sortedData[date] = [];
            }
          });

        setData(sortedData);
      } catch (err: any) {
        setError(err.message || t("An error occurred fetching earnings data."));
      } finally {
        setLoading(false);
      }
    };

    fetchEarnings();
  }, [apiUrl]);

  const filteredData = React.useMemo(() => {
    if (!data) return null;

    const filtered: EarningsCalendarResponse = {};
    Object.entries(data).forEach(([date, events]) => {
      const matchEvents = events.filter((event) => {
        const matchesSearch =
          event.ticker.toLowerCase().includes(searchQuery.toLowerCase()) ||
          event.company_name.toLowerCase().includes(searchQuery.toLowerCase());

        let matchesImpact = true;
        if (impactFilter !== "all") {
          const impactLevel =
            event.importance >= 4
              ? "high"
              : event.importance >= 3
                ? "medium"
                : "low";
          matchesImpact = impactLevel === impactFilter;
        }

        return matchesSearch && matchesImpact;
      });

      if (matchEvents.length > 0) {
        filtered[date] = matchEvents;
      }
    });
    return filtered;
  }, [data, searchQuery, impactFilter]);

  return (
    <Card
      className={cn(
        " text-white   overflow-hidden flex flex-col w-full h-full shadow-[0_25px_60px_rgba(0,0,0,0.6)] antialiased",
        className,
      )}
      style={{ width }}
    >
      <CardHeader className="flex !p-3 px-6 justify-between  gap-4 flex-row items-center w-full ">
        <CardTitle className="text-xl flex gap-0 items-start text-nowrap flex-col w-full">
          <div className="flex items-center gap-2 justify-between w-full">
            <p>{t("Earnings Signals")}</p>
          </div>
        </CardTitle>
        {badgeTitle && onBadgeClick && (
          <p
            onClick={onBadgeClick}
            className="!mt-0 text-sm flex items-center text-[#2252CC] cursor-pointer whitespace-nowrap"
          >
            {badgeTitle}
            <ExternalLinkIcon className="h-[15px] ml-1" />
          </p>
        )}
      </CardHeader>
      <p className="text-sm !px-3 text-muted-foreground font-medium mt-1">
        {t("A data-driven model for high-impact events")}
      </p>
      <div className="overflow-y-auto flex-1 px-6 flex flex-col gap-6 custom-scrollbar">
        {loading && (
          <div className="text-center text-white/20 py-16 px-4 text-sm font-medium animate-pulse">
            {t("Loading earnings...")}
          </div>
        )}

        {error && !loading && (
          <div className="text-center text-rose-500 py-16 px-4 text-sm font-bold uppercase tracking-wider">
            {error}
          </div>
        )}

        {!loading &&
          !error &&
          filteredData &&
          Object.keys(filteredData).length === 0 && (
            <div className="text-center text-white/20 py-16 px-4 text-sm font-medium">
              {t("No upcoming earnings scheduled.")}
            </div>
          )}

        {!loading &&
          !error &&
          filteredData &&
          Object.entries(filteredData).map(([date, events], index) => (
            <div key={date} className="flex flex-col gap-6">
              <div className="sticky top-0 z-20 -mx-6 px-6 py-3 bg-[#060B18]/80 backdrop-blur-xl flex items-center gap-5 border-b border-white/[0.03]">
                <span className="text-[10px] font-black text-blue-400 uppercase tracking-[0.25em]">
                  {formatDate(date)}
                </span>
                <div className="h-px bg-blue-500/10 flex-1" />
              </div>

              <div className="grid grid-cols-1 gap-5">
                {events.map((event, idx) => (
                  <EarningsCard
                    key={`${event.ticker}-${idx}`}
                    event={event}
                    logoUrl={logos[event.ticker]}
                    onTrade={handleTrade}
                    CTALoading={loadingTicker === event.ticker}
                  />
                ))}
              </div>
            </div>
          ))}
      </div>

      <div
        className={cn(
          "px-6 py-2 flex justify-end items-center z-50 shrink-0 bg-transparent absolute  bottom-0 right-0",
          user && "hidden",
        )}
      >
        <motion.a
          href="https://trylevel2.com"
          target="_top"
          className="flex items-center gap-2.5 bg-muted-foreground/20 hover:bg-muted-foreground/20 backdrop-blur-md p-1.5 rounded-full border border-white/[0.05] transition-colors group overflow-hidden"
          initial={{ width: 44 }}
          whileHover={{ width: 100 }}
          transition={{ type: "keyframes" }}
        >
          <div className="flex items-center gap-3 flex-nowrap min-w-max">
            <div className="w-8 h-8 rounded-full bg-[--accent-primary] flex items-center justify-center shrink-0 shadow-xl overflow-hidden">
              <img
                src={"/images/logos/Symbol_Dark.png"}
                className="w-5 h-5 object-contain "
                alt="L2"
                onError={(e) => {
                  (e.target as HTMLImageElement).src =
                    "https://trylevel2.com/favicon.ico";
                }}
              />
            </div>
            <span className="text-white hover:text-blue-400 text-sm font-bold opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none pr-1">
              {details.brokerName}
            </span>
          </div>
        </motion.a>
      </div>
    </Card>
  );
};
